import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface Language {
  code: string;
  name: string;
  native_name: string;
  is_rtl: boolean;
  is_active: boolean;
  display_order: number;
}

interface IndustryDomain {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  display_order: number;
}

interface Company {
  id: number;
  name: string;
  description: string;
  website: string;
  email: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  industry: string;
  company_size: string;
  founded_year: number;
  status: 'active' | 'suspended' | 'inactive';
  subscription_plan: string;
  subscription_end_date: string;
  total_credits: number;
  used_credits: number;
  remaining_credits: number;
  user_count: number;
  created_at: string;
  updated_at: string;
  available_languages?: string[];
  available_languages_detail?: Language[];
  default_language?: string;
  default_language_detail?: Language;
  industry_domain?: number | null;
  industry_domain_detail?: IndustryDomain | null;
  logo?: string | null;
  total_tokens?: number;
  total_token_cost?: number;
}

interface CompanyFormData {
  name: string;
  description: string;
  website: string | null;
  email: string;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code: string | null;
  industry: string | null;
  company_size: string | null;
  founded_year: number | null;
  status: 'active' | 'suspended' | 'inactive';
  subscription_plan: string;
  subscription_end_date: string | null;
  available_languages: string[];
  default_language: string | null;
  industry_domain: number | null;
  logo?: File | string | null;
}

interface FormErrors {
  name?: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  industry?: string;
  company_size?: string;
  founded_year?: string;
  status?: string;
  subscription_plan?: string;
  subscription_end_date?: string;
  logo?: string;
  available_languages?: string;
  default_language?: string;
  industry_domain?: string;
}

const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [industryFilter, setIndustryFilter] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    description: '',
    website: null,
    email: '',
    phone: null,
    address_line1: null,
    address_line2: null,
    city: null,
    state: null,
    country: null,
    postal_code: null,
    industry: null,
    company_size: null,
    founded_year: new Date().getFullYear(),
    status: 'active',
    subscription_plan: 'basic',
    subscription_end_date: null,
    available_languages: [],
    default_language: null,
    industry_domain: null,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [industryDomains, setIndustryDomains] = useState<IndustryDomain[]>([]);
  const [loadingIndustryDomains, setLoadingIndustryDomains] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const formatApiErrorValue = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(formatApiErrorValue).filter(Boolean).join(' ');
    }
    if (typeof value === 'object') {
      return Object.values(value as Record<string, unknown>)
        .map(formatApiErrorValue)
        .filter(Boolean)
        .join(' ');
    }
    return String(value);
  };

  const parseCompanySaveError = (error: any): { fieldErrors: FormErrors; message: string } => {
    const data = error?.response?.data;
    const fallback =
      error?.message ||
      (editingCompany ? 'Failed to update company' : 'Failed to save company');

    if (!data) {
      return { fieldErrors: {}, message: fallback };
    }

    if (typeof data === 'string') {
      return { fieldErrors: {}, message: data };
    }

    const fieldErrors: FormErrors = {};
    const generalParts: string[] = [];

    Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
      const message = formatApiErrorValue(value);
      if (!message) return;

      if (key in formData) {
        fieldErrors[key as keyof FormErrors] = message;
      } else if (['detail', 'error', 'message', 'non_field_errors'].includes(key)) {
        generalParts.push(message);
      } else {
        // Unknown keys (e.g. nested validation) still need to be visible
        generalParts.push(`${key}: ${message}`);
      }
    });

    // Prefer explicit general message; otherwise summarize field errors
    const message =
      generalParts.join(' ') ||
      (Object.keys(fieldErrors).length > 0
        ? Object.entries(fieldErrors)
            .map(([key, msg]) => `${key}: ${msg}`)
            .join('; ')
        : formatApiErrorValue(data) || fallback);

    return { fieldErrors, message };
  };

  const companySizes = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1001-5000',
    '5000+',
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Manufacturing',
    'Retail',
    'Consulting',
    'Other',
  ];

  const subscriptionPlans = [
    'basic',
    'premium',
    'enterprise',
  ];

  const fetchLanguages = async () => {
    setLoadingLanguages(true);
    try {
      const response = await api.get('/languages/');
      setLanguages(response.data.results || response.data || []);
    } catch (error: any) {
      console.error('Error fetching languages:', error);
    } finally {
      setLoadingLanguages(false);
    }
  };

  const fetchIndustryDomains = async () => {
    setLoadingIndustryDomains(true);
    try {
      const response = await api.get('/industry-domains/');
      setIndustryDomains(response.data.results || response.data || []);
    } catch (error: any) {
      console.error('Error fetching industry domains:', error);
    } finally {
      setLoadingIndustryDomains(false);
    }
  };

  useEffect(() => {
    fetchLanguages();
    fetchIndustryDomains();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        page_size: rowsPerPage.toString(),
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      if (industryFilter !== 'all') {
        params.append('industry', industryFilter);
      }

      const response = await api.get(`/super-admin/companies/?${params}`);
      console.log('Companies response:', response.data);

      setCompanies(response.data.results || response.data);
      setTotalCount(response.data.count || response.data.length);
    } catch (error: any) {
      console.error('Error fetching companies:', error);
      setError(
        error.response?.data?.error || 
        error.message || 
        'Failed to fetch companies'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [page, rowsPerPage, searchTerm, statusFilter, industryFilter]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleIndustryFilterChange = (event: any) => {
    setIndustryFilter(event.target.value);
    setPage(0);
  };

  const handleAddCompany = () => {
    setEditingCompany(null);
    const englishLang = languages.find(l => l.code === 'en' || l.name === 'English');
    const defaultLangs = englishLang ? [englishLang.code] : [];
    const defaultLang = englishLang ? englishLang.code : null;
    setFormData({
      name: '',
      description: '',
      website: null,
      email: '',
      phone: null,
      address_line1: null,
      address_line2: null,
      city: null,
      state: null,
      country: null,
      postal_code: null,
      industry: null,
      company_size: null,
      founded_year: new Date().getFullYear(),
      status: 'active',
      subscription_plan: 'basic',
      subscription_end_date: null,
      available_languages: defaultLangs,
      default_language: defaultLang,
      industry_domain: null,
      logo: null,
    });
    setFormErrors({});
    setSubmitError(null);
    setLogoPreview(null);
    setDialogOpen(true);
  };

  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      description: company.description,
      website: company.website,
      email: company.email,
      phone: company.phone,
      address_line1: company.address_line1,
      address_line2: company.address_line2,
      city: company.city,
      state: company.state,
      country: company.country,
      postal_code: company.postal_code,
      industry: company.industry,
      company_size: company.company_size,
      founded_year: company.founded_year,
      status: company.status,
      subscription_plan: company.subscription_plan,
      subscription_end_date: company.subscription_end_date,
      available_languages: company.available_languages || [],
      default_language: company.default_language || null,
      industry_domain: company.industry_domain || null,
      logo: company.logo || null,
    });
    setFormErrors({});
    setSubmitError(null);
    // Set logo preview if logo exists
    if (company.logo) {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const logoUrl = company.logo.startsWith('http') 
        ? company.logo 
        : `${apiUrl.replace(/\/api\/?$/, '')}/${company.logo.replace(/^\//, '')}`;
      setLogoPreview(logoUrl);
    } else {
      setLogoPreview(null);
    }
    setDialogOpen(true);
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/super-admin/companies/${companyId}/`);
      fetchCompanies();
    } catch (error: any) {
      console.error('Error deleting company:', error);
      alert('Failed to delete company: ' + (error.response?.data?.error || error.message));
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Company name is required';
    }

    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (formData.website?.trim() && !/^https?:\/\/.+/.test(formData.website)) {
      errors.website = 'Website must be a valid URL';
    }

    if (formData.founded_year && (formData.founded_year < 1800 || formData.founded_year > new Date().getFullYear())) {
      errors.founded_year = 'Founded year must be between 1800 and current year';
    }

    // Validate subscription end date if provided
    if (formData.subscription_end_date?.trim() && !/^\d{4}-\d{2}-\d{2}$/.test(formData.subscription_end_date)) {
      errors.subscription_end_date = 'Date must be in YYYY-MM-DD format';
    }

    // Validate default language is in available languages
    if (formData.default_language && formData.available_languages.length > 0) {
      if (!formData.available_languages.includes(formData.default_language)) {
        errors.default_language = 'Default language must be one of the available languages';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Check if we have a file upload (logo)
      const hasFileUpload = formData.logo instanceof File;

      if (hasFileUpload) {
        // Use FormData for file uploads
        const formDataToSend = new FormData();
        
        // Append all form fields
        Object.keys(formData).forEach(key => {
          const value = formData[key as keyof CompanyFormData];
          if (key === 'logo' && value instanceof File) {
            formDataToSend.append('logo', value);
          } else if (key === 'logo' && !value) {
            // Skip logo if it's null/empty and not a file
            // Don't append anything for logo if it's null
          } else if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              value.forEach(item => formDataToSend.append(key, item));
            } else if (typeof value === 'object') {
              formDataToSend.append(key, JSON.stringify(value));
            } else {
              formDataToSend.append(key, value.toString());
            }
          }
        });

        if (editingCompany) {
          await api.put(`/super-admin/companies/${editingCompany.id}/`, formDataToSend, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } else {
          await api.post('/super-admin/companies/', formDataToSend, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        }
      } else {
        // Use JSON for non-file updates
        const submitData = {
          ...formData,
          subscription_end_date: formData.subscription_end_date || null,
          logo: formData.logo || undefined, // Only include logo if it's a string (existing URL)
        };

        if (editingCompany) {
          await api.put(`/super-admin/companies/${editingCompany.id}/`, submitData);
        } else {
          await api.post('/super-admin/companies/', submitData);
        }
      }

      setDialogOpen(false);
      setSubmitError(null);
      setLogoPreview(null);
      fetchCompanies();
    } catch (error: any) {
      console.error('Error saving company:', error);
      const { fieldErrors, message } = parseCompanySaveError(error);
      setFormErrors(fieldErrors);
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormChange = (field: keyof CompanyFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (submitError) {
      setSubmitError(null);
    }
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    // Handle logo preview
    if (field === 'logo' && value instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(value);
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (PNG, JPG, or SVG)');
        return;
      }
      
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo file size must be less than 2MB');
        return;
      }
      
      handleFormChange('logo', file);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'warning';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && companies.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Companies Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all companies in the system
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCompany}
        >
          Add Company
        </Button>
      </Box>

             {/* Filters */}
       <Card sx={{ mb: 3 }}>
         <CardContent>
           <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <TextField
                 fullWidth
                 placeholder="Search companies..."
                 value={searchTerm}
                 onChange={handleSearch}
                 InputProps={{
                   startAdornment: (
                     <InputAdornment position="start">
                       <SearchIcon />
                     </InputAdornment>
                   ),
                 }}
               />
             </Box>
             <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
               <FormControl fullWidth>
                 <InputLabel>Status</InputLabel>
                 <Select
                   value={statusFilter}
                   onChange={handleStatusFilterChange}
                   label="Status"
                 >
                   <MenuItem value="all">All Statuses</MenuItem>
                   <MenuItem value="active">Active</MenuItem>
                   <MenuItem value="suspended">Suspended</MenuItem>
                   <MenuItem value="inactive">Inactive</MenuItem>
                 </Select>
               </FormControl>
             </Box>
             <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
               <FormControl fullWidth>
                 <InputLabel>Industry</InputLabel>
                 <Select
                   value={industryFilter}
                   onChange={handleIndustryFilterChange}
                   label="Industry"
                 >
                   <MenuItem value="all">All Industries</MenuItem>
                   {industries.map(industry => (
                     <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Box>
             <Box>
               <Tooltip title="Refresh">
                 <IconButton onClick={fetchCompanies} color="primary">
                   <RefreshIcon />
                 </IconButton>
               </Tooltip>
             </Box>
           </Box>
         </CardContent>
       </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Companies Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Industry</TableCell>
                <TableCell>Industry Domain</TableCell>
                <TableCell>Languages</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Tokens</TableCell>
                <TableCell>AI Cost</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {company.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {company.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={company.status}
                      color={getStatusColor(company.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {company.industry}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {company.industry_domain_detail ? (
                      <Chip 
                        label={company.industry_domain_detail.name} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not set
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {(() => {
                        // Get languages from detail if available, otherwise look up from codes
                        let languagesToShow: Language[] = [];
                        if (company.available_languages_detail && company.available_languages_detail.length > 0) {
                          languagesToShow = company.available_languages_detail;
                        } else if (company.available_languages && company.available_languages.length > 0) {
                          languagesToShow = company.available_languages
                            .map(code => languages.find(lang => lang.code === code))
                            .filter((lang): lang is Language => lang !== undefined);
                        }
                        
                        if (languagesToShow.length > 0) {
                          return languagesToShow.map((lang) => {
                            const isDefault = company.default_language === lang.code;
                            return (
                              <Chip
                                key={lang.code}
                                label={lang.name}
                                size="small"
                                color={isDefault ? 'primary' : 'default'}
                                sx={{
                                  backgroundColor: isDefault ? 'primary.main' : 'action.selected',
                                  color: isDefault ? 'primary.contrastText' : 'text.primary',
                                  fontWeight: isDefault ? 'bold' : 'normal',
                                }}
                              />
                            );
                          });
                        } else {
                          return (
                            <Typography variant="caption" color="text.secondary">
                              No languages
                            </Typography>
                          );
                        }
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {company.user_count}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2" color="textPrimary">
                        {company.total_credits}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Used: {company.used_credits}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {(company.total_tokens || 0).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    ${(company.total_token_cost || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">
                        {company.subscription_plan}
                      </Typography>
                      {company.subscription_end_date && (
                        <Typography variant="caption" color="textSecondary">
                          Until {new Date(company.subscription_end_date).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" textTransform="capitalize">
                      {company.subscription_plan}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(company.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditCompany(company)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCompany(company.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSubmitError(null);
          setLogoPreview(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingCompany ? 'Edit Company' : 'Add New Company'}
        </DialogTitle>
                 <DialogContent>
           {submitError && (
             <Alert severity="error" sx={{ mt: 1, mb: 2 }} onClose={() => setSubmitError(null)}>
               {submitError}
             </Alert>
           )}
           <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <TextField
                 fullWidth
                 label="Company Name"
                 value={formData.name}
                 onChange={(e) => handleFormChange('name', e.target.value)}
                 error={!!formErrors.name}
                 helperText={formErrors.name}
                 required
               />
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <TextField
                 fullWidth
                 label="Email"
                 type="email"
                 value={formData.email}
                 onChange={(e) => handleFormChange('email', e.target.value)}
                 error={!!formErrors.email}
                 helperText={formErrors.email}
                 required
               />
             </Box>
             <Box sx={{ flex: '1 1 100%' }}>
               <TextField
                 fullWidth
                 label="Description"
                 multiline
                 rows={3}
                 value={formData.description}
                 onChange={(e) => handleFormChange('description', e.target.value)}
               />
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <TextField
                 fullWidth
                 label="Website"
                 value={formData.website || ''}
                 onChange={(e) => handleFormChange('website', e.target.value || null)}
                 error={!!formErrors.website}
                 helperText={formErrors.website}
               />
             </Box>
             <Box sx={{ flex: '1 1 100%' }}>
               <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                 Company Logo
               </Typography>
               <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                 Recommended: 200px × 60px, max 2MB. Formats: PNG, JPG, SVG
               </Typography>
               <input
                 accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                 style={{ display: 'none' }}
                 id="logo-upload"
                 type="file"
                 onChange={handleLogoChange}
               />
               <label htmlFor="logo-upload">
                 <Button
                   variant="outlined"
                   component="span"
                   sx={{ mb: 2 }}
                 >
                   {formData.logo instanceof File ? 'Change Logo' : logoPreview ? 'Change Logo' : 'Upload Logo'}
                 </Button>
               </label>
               {logoPreview && (
                 <Box sx={{ mt: 2 }}>
                   <img
                     src={logoPreview}
                     alt="Logo preview"
                     style={{
                       maxWidth: '200px',
                       maxHeight: '60px',
                       objectFit: 'contain',
                       border: '1px solid var(--border-subtle)',
                       borderRadius: 'var(--radius-sm)',
                       padding: '8px',
                       backgroundColor: 'var(--bg-surface)'
                     }}
                   />
                 </Box>
               )}
               {formErrors.logo && (
                 <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                   {formErrors.logo}
                 </Typography>
               )}
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <TextField
                 fullWidth
                 label="Phone"
                 value={formData.phone || ''}
                 onChange={(e) => handleFormChange('phone', e.target.value || null)}
               />
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <TextField
                 fullWidth
                 label="Industry"
                 value={formData.industry || ''}
                 onChange={(e) => handleFormChange('industry', e.target.value || null)}
               />
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <FormControl fullWidth>
                 <InputLabel>Industry Domain</InputLabel>
                 <Select
                   value={formData.industry_domain ? formData.industry_domain.toString() : ''}
                   onChange={(e) => {
                     const value = e.target.value;
                     handleFormChange('industry_domain', value ? parseInt(value, 10) : null);
                   }}
                   label="Industry Domain"
                   disabled={loadingIndustryDomains}
                 >
                   <MenuItem value="">
                     <em>None (Use default IT prompts)</em>
                   </MenuItem>
                   {industryDomains.map(domain => (
                     <MenuItem key={domain.id} value={domain.id.toString()}>
                       {domain.name} ({domain.code})
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <FormControl fullWidth>
                 <InputLabel>Company Size</InputLabel>
                 <Select
                   value={formData.company_size || ''}
                   onChange={(e) => handleFormChange('company_size', e.target.value || null)}
                   label="Company Size"
                 >
                   {companySizes.map(size => (
                     <MenuItem key={size} value={size}>{size}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <TextField
                 fullWidth
                 label="Founded Year"
                 type="number"
                 value={formData.founded_year || ''}
                 onChange={(e) => handleFormChange('founded_year', parseInt(e.target.value) || null)}
                 error={!!formErrors.founded_year}
                 helperText={formErrors.founded_year}
               />
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <FormControl fullWidth>
                 <InputLabel>Status</InputLabel>
                 <Select
                   value={formData.status}
                   onChange={(e) => handleFormChange('status', e.target.value)}
                   label="Status"
                 >
                   <MenuItem value="active">Active</MenuItem>
                   <MenuItem value="suspended">Suspended</MenuItem>
                   <MenuItem value="inactive">Inactive</MenuItem>
                 </Select>
               </FormControl>
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <FormControl fullWidth>
                 <InputLabel>Subscription Plan</InputLabel>
                 <Select
                   value={formData.subscription_plan}
                   onChange={(e) => handleFormChange('subscription_plan', e.target.value)}
                   label="Subscription Plan"
                 >
                   {subscriptionPlans.map(plan => (
                     <MenuItem key={plan} value={plan}>{plan}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <TextField
                 fullWidth
                 label="Subscription End Date"
                 type="date"
                 value={formData.subscription_end_date || ''}
                 onChange={(e) => handleFormChange('subscription_end_date', e.target.value || null)}
                 InputLabelProps={{ shrink: true }}
               />
             </Box>
             <Box sx={{ flex: '1 1 100%' }}>
               <TextField
                 fullWidth
                 label="Address Line 1"
                 value={formData.address_line1 || ''}
                 onChange={(e) => handleFormChange('address_line1', e.target.value || null)}
               />
             </Box>
             <Box sx={{ flex: '1 1 100%' }}>
               <TextField
                 fullWidth
                 label="Address Line 2"
                 value={formData.address_line2 || ''}
                 onChange={(e) => handleFormChange('address_line2', e.target.value || null)}
               />
             </Box>
             <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
               <TextField
                 fullWidth
                 label="City"
                 value={formData.city || ''}
                 onChange={(e) => handleFormChange('city', e.target.value || null)}
               />
             </Box>
             <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
               <TextField
                 fullWidth
                 label="State"
                 value={formData.state || ''}
                 onChange={(e) => handleFormChange('state', e.target.value || null)}
               />
             </Box>
             <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
               <TextField
                 fullWidth
                 label="Postal Code"
                 value={formData.postal_code || ''}
                 onChange={(e) => handleFormChange('postal_code', e.target.value || null)}
               />
             </Box>
             <Box sx={{ flex: '1 1 100%' }}>
               <TextField
                 fullWidth
                 label="Country"
                 value={formData.country || ''}
                 onChange={(e) => handleFormChange('country', e.target.value || null)}
               />
             </Box>
             <Box sx={{ flex: '1 1 100%' }}>
               <FormControl fullWidth error={!!formErrors.available_languages}>
                 <InputLabel>Available Languages</InputLabel>
                 <Select
                   multiple
                   value={formData.available_languages}
                   onChange={(e) => {
                     const value = e.target.value as string[];
                     handleFormChange('available_languages', value);
                     // If default language is not in selected languages, clear it
                     if (formData.default_language && !value.includes(formData.default_language)) {
                       handleFormChange('default_language', null);
                     }
                   }}
                   input={<OutlinedInput label="Available Languages" />}
                   renderValue={(selected) => {
                     const selectedLangs = languages.filter(lang => selected.includes(lang.code));
                     return selectedLangs.map(lang => lang.name).join(', ');
                   }}
                 >
                   {languages.map((language) => (
                     <MenuItem key={language.code} value={language.code}>
                       <Checkbox checked={formData.available_languages.indexOf(language.code) > -1} />
                       <ListItemText 
                         primary={`${language.name} (${language.native_name})`}
                         secondary={language.code}
                       />
                     </MenuItem>
                   ))}
                 </Select>
                 {formErrors.available_languages && (
                   <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
                     {formErrors.available_languages}
                   </Typography>
                 )}
               </FormControl>
             </Box>
             <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
               <FormControl fullWidth error={!!formErrors.default_language}>
                 <InputLabel>Default Language</InputLabel>
                 <Select
                   value={formData.default_language || ''}
                   onChange={(e) => handleFormChange('default_language', e.target.value || null)}
                   label="Default Language"
                   disabled={formData.available_languages.length === 0}
                 >
                   <MenuItem value="">None</MenuItem>
                   {languages
                     .filter(lang => formData.available_languages.length === 0 || formData.available_languages.includes(lang.code))
                     .map((language) => (
                       <MenuItem key={language.code} value={language.code}>
                         {language.name} ({language.native_name})
                       </MenuItem>
                     ))}
                 </Select>
                 {formErrors.default_language && (
                   <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
                     {formErrors.default_language}
                   </Typography>
                 )}
               </FormControl>
             </Box>
           </Box>
         </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setSubmitError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : undefined}
          >
            {submitting ? 'Saving...' : (editingCompany ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Companies;
